import { useStyletron } from 'parkdepot-shared/theme'
import Button from 'parkdepot-shared/components/Buttons/Button'
import { BUTTON_KIND, BUTTON_SIZE } from 'parkdepot-shared/components/Buttons/types'
import React, { ChangeEvent, FC, ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

// style components
import { guidelinesRoute } from '../../views/Guidelines/route'
import { loginRoute } from '../../views/Login/route.const'
import Text from '../style/Text.style'
import {
    FormContainer,
    InvalidPasswordIcon,
    InvalidPasswordLine,
    InvalidPasswordLineTitle,
    InvalidPasswordOtherWrapper,
    InvalidPasswordText,
    InvalidPasswordWrapper,
    InputContainer,
    InputPlaceholder,
    Title,
    ColoredLink,
    SeePasswordIcon,
    LinksContainer,
    BlackLink
} from './RegisterForm.style'

interface Props {
    onRegisterClick: (email: string, password: string, name: string, lastName: string) => void
    loading: boolean
    setLoading: (loading: boolean) => void
    onRegisterError?: string
    loginReturnUrl?: string
    subtitle?: ReactNode
}

const RegisterForm: FC<Props> = ({ onRegisterClick, loading, loginReturnUrl, subtitle }) => {
    const { t } = useTranslation()
    const [css, theme] = useStyletron()
    const [name, setName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [hiddenPassword, setHiddenPassword] = useState(true)
    // const [repeatPassword, setRepeatPassword] = useState('')
    const [passwordRules, setPasswordRules] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        otherValid: false,
        valid: false
    })
    const [passwordTouched, setPasswordTouched] = useState(false)
    const loginRouteStr = loginReturnUrl
        ? `${loginRoute()}?returnUrl=${encodeURIComponent(loginReturnUrl)}`
        : loginRoute()

    // password rules:
    // 1. at least 8 characters
    // 2. at least three of the following:
    //    - uppercase letter
    //    - lowercase letter
    //    - number
    //    - special character

    // const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/

    const checkPassword = (password: string) => {
        const length = password.length >= 8
        const uppercase = /[A-Z]/.test(password)
        const lowercase = /[a-z]/.test(password)
        const number = /\d/.test(password)
        const special = /[!@#$%^&*()_+]/.test(password)

        const otherValid = [uppercase, lowercase, number, special].filter((rule) => rule).length >= 3
        const valid = length && otherValid

        setPasswordRules({
            length,
            uppercase,
            lowercase,
            number,
            special,
            valid,
            otherValid
        })
    }

    const onSubmit = () => {
        onRegisterClick(email, password, name, lastName)
    }

    return (
        <>
            <Title>
                <Text typography={theme.typography.HeadingMedium} fontWeight={600}>
                    {t('register.title')}
                </Text>
            </Title>

            {subtitle}

            <form
                autoComplete="on"
                onSubmit={(e) => {
                    e.preventDefault()
                    onSubmit()
                }}
            >
                <FormContainer>
                    <div>
                        <InputPlaceholder>{t('register.name')}</InputPlaceholder>
                        <InputContainer
                            name="name"
                            autoComplete="given-name"
                            type="text"
                            value={name}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setName(e.target.value)
                            }}
                            placeholder={t('register.yourName')}
                        />
                    </div>
                    <div>
                        <InputPlaceholder>{t('register.lastName')}</InputPlaceholder>
                        <InputContainer
                            name="lastName"
                            autoComplete="family-name"
                            type="text"
                            value={lastName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setLastName(e.target.value)
                            }}
                            placeholder={t('register.yourLastName')}
                        />
                    </div>
                    <div>
                        <InputPlaceholder>{t('login.email')}</InputPlaceholder>
                        <InputContainer
                            type="email"
                            name="email"
                            autoComplete="username"
                            value={email}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setEmail(e.target.value)
                            }}
                            placeholder={t('login.yourEmail')}
                        />
                    </div>
                    <div className={css({ position: 'relative' })}>
                        <InputPlaceholder htmlFor="new-password">{t('login.password')}</InputPlaceholder>
                        <InputContainer
                            type={hiddenPassword ? 'password' : 'text'}
                            id="new-password"
                            name="new-password"
                            onBlur={() => {
                                if (!passwordTouched) {
                                    setPasswordTouched(true)
                                }
                            }}
                            $error={passwordTouched && !passwordRules.valid}
                            autoComplete="new-password"
                            placeholder={t('login.yourPassword')}
                            value={password}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setPassword(e.target.value)
                                checkPassword(e.target.value)
                            }}
                        />
                        {password && (
                            <SeePasswordIcon
                                className={`wemolo-eye-${hiddenPassword ? 'open' : 'closed'}`}
                                onClick={() => setHiddenPassword(!hiddenPassword)}
                            />
                        )}
                    </div>
                    {/* <div>
                        <InputPlaceholder htmlFor="repeat-password">{t('Repeat password')}</InputPlaceholder>
                        <InputContainer
                            type="password"
                            id="repeat-password"
                            name="repeat-password"
                            placeholder={t('Repeat password')}
                            value={repeatPassword}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setRepeatPassword(e.target.value)
                            }}
                        />
                    </div> */}
                    {passwordTouched && !passwordRules.valid && (
                        <InvalidPasswordWrapper>
                            <InvalidPasswordLineTitle>{t('register.passwordMustContain')}</InvalidPasswordLineTitle>
                            <InvalidPasswordLine>
                                <InvalidPasswordIcon
                                    $valid={passwordRules.length}
                                    className={`${passwordRules.length ? 'wemolo-check' : 'wemolo-cross'}`}
                                />
                                <InvalidPasswordText $valid={passwordRules.length}>
                                    {t('register.leastEightCharacters')}
                                </InvalidPasswordText>
                            </InvalidPasswordLine>

                            <InvalidPasswordLine>
                                <InvalidPasswordIcon
                                    $valid={passwordRules.otherValid}
                                    className={`${passwordRules.otherValid ? 'wemolo-check' : 'wemolo-cross'}`}
                                />
                                <InvalidPasswordText $valid={passwordRules.otherValid}>
                                    {t('register.leastThreeOfTheFollowing')}
                                </InvalidPasswordText>
                            </InvalidPasswordLine>
                            <InvalidPasswordOtherWrapper>
                                <InvalidPasswordLine>
                                    <InvalidPasswordIcon
                                        $valid={passwordRules.lowercase}
                                        className={`${passwordRules.lowercase ? 'wemolo-check' : 'wemolo-cross'}`}
                                        $black={passwordRules.otherValid}
                                    />
                                    <InvalidPasswordText
                                        $valid={passwordRules.lowercase}
                                        $black={passwordRules.otherValid}
                                    >
                                        {t('register.lowerCaseLetters')}
                                    </InvalidPasswordText>
                                </InvalidPasswordLine>
                                <InvalidPasswordLine>
                                    <InvalidPasswordIcon
                                        $valid={passwordRules.uppercase}
                                        className={`${passwordRules.uppercase ? 'wemolo-check' : 'wemolo-cross'}`}
                                        $black={passwordRules.otherValid}
                                    />
                                    <InvalidPasswordText
                                        $valid={passwordRules.uppercase}
                                        $black={passwordRules.otherValid}
                                    >
                                        {t('register.upperCaseLetters')}
                                    </InvalidPasswordText>
                                </InvalidPasswordLine>
                                <InvalidPasswordLine>
                                    <InvalidPasswordIcon
                                        $valid={passwordRules.number}
                                        className={`${passwordRules.number ? 'wemolo-check' : 'wemolo-cross'}`}
                                        $black={passwordRules.otherValid}
                                    />
                                    <InvalidPasswordText
                                        $valid={passwordRules.number}
                                        $black={passwordRules.otherValid}
                                    >
                                        {t('register.numbers')}
                                    </InvalidPasswordText>
                                </InvalidPasswordLine>
                                <InvalidPasswordLine>
                                    <InvalidPasswordIcon
                                        $valid={passwordRules.special}
                                        className={`${passwordRules.special ? 'wemolo-check' : 'wemolo-cross'}`}
                                        $black={passwordRules.otherValid}
                                    />
                                    <InvalidPasswordText
                                        $valid={passwordRules.special}
                                        $black={passwordRules.otherValid}
                                    >
                                        {t('register.specialCharacters')}
                                    </InvalidPasswordText>
                                </InvalidPasswordLine>
                            </InvalidPasswordOtherWrapper>
                        </InvalidPasswordWrapper>
                    )}
                </FormContainer>
                <div
                    className={css({
                        textAlign: 'center'
                    })}
                >
                    <Button
                        id="register-btn"
                        type="submit"
                        isLoading={loading}
                        disabled={!email || !password || !name || !lastName || !passwordRules.valid}
                        $size={BUTTON_SIZE.medium}
                        $kind={BUTTON_KIND.primary}
                        overrides={{
                            BaseButton: {
                                style: {
                                    width: '100%'
                                }
                            }
                        }}
                    >
                        <div>{t('register.registerButton')}</div>
                    </Button>
                </div>
            </form>
            <LinksContainer>
                <Text typography={theme.typography.ParagraphXSmall} fontWeight={300}>
                    <Trans
                        i18nKey="register.confirmTerms"
                        components={{
                            1: <BlackLink to={guidelinesRoute()} target="_blank" />,
                            2: <BlackLink to={guidelinesRoute()} target="_blank" />
                        }}
                    ></Trans>
                </Text>
            </LinksContainer>
            <div>
                <Text typography={theme.typography.ParagraphMedium} fontWeight={300}>
                    <Trans
                        i18nKey="register.existingAccount"
                        components={{
                            1: <ColoredLink to={loginRouteStr} />
                        }}
                    ></Trans>
                </Text>
            </div>
        </>
    )
}

export default RegisterForm
